import { StaggerGroup } from '@/shared/ui/animations';

import { StackedHeader, SplitHeaderV2, DetailHeader, BackHeader } from './headers';
import type { SectionLayoutProps } from './SectionLayout.types';

/**
 * The section's centred content column. Exported so content printed outside a section, such
 * as the IQ export's report header, lines up with the section's edges.
 */
export const SECTION_COLUMN_CLASS = 'mx-auto w-full min-w-0 max-w-[1024px]';

const HEADER = {
  stacked: StackedHeader,
  split: SplitHeaderV2,
  detail: DetailHeader,
  back: BackHeader,
} as const;

/**
 * Stagger slots each header variant animates, so the content below can cascade
 * after the header. Keep in sync with each header's `StaggerItem` count: the
 * title always animates; `stacked` adds a slot for the subtitle, `detail` and `back` for
 * their actions. `split` animates only the title (its actions are static).
 */
const headerStaggerSlots = ({
  variant,
  subtitle,
  actions,
}: Pick<SectionLayoutProps, 'variant' | 'subtitle' | 'actions'>): number => {
  switch (variant) {
    case 'stacked':
      return subtitle ? 2 : 1;
    case 'detail':
    case 'back':
      return actions ? 2 : 1;
    default:
      return 1;
  }
};

/**
 * Redesigned section layout: the content cascades *after* the header (offset by
 * the header's stagger slots) rather than animating alongside it, and the
 * `split` variant uses the redesigned header. The original {@link SectionLayout}
 * stays untouched for every other consumer.
 */
export const SectionLayoutV2 = ({
  variant = 'stacked',
  children,
  ...headerProps
}: SectionLayoutProps) => {
  const Header = HEADER[variant];

  // `mx-auto`, not `self-center`: this renders under a flex parent on the live page and a block
  // one on the print route, and `align-self` does nothing in the latter.
  return (
    <div className={`${SECTION_COLUMN_CLASS} flex min-h-full flex-col gap-10`}>
      <Header {...headerProps} />
      <StaggerGroup
        startIndex={headerStaggerSlots({
          variant,
          subtitle: headerProps.subtitle,
          actions: headerProps.actions,
        })}
      >
        <div className="flex flex-1 flex-col pb-4">{children}</div>
      </StaggerGroup>
    </div>
  );
};

SectionLayoutV2.displayName = 'SectionLayoutV2';
